import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Plus, Edit2, Trash2, Loader2, X, Star } from 'lucide-react';

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
  
  h1 {
    font-size: 2rem;
  }
`;

const Button = styled.button`
  background: var(--primary);
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 8px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  transition: opacity 0.3s;
  
  &:hover {
    opacity: 0.9;
  }
  
  &.danger {
    background: #ef4444;
  }
  
  &.secondary {
    background: transparent;
    border: 1px solid var(--glass-border);
    color: var(--text-main);
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  background: var(--bg-card);
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid var(--glass-border);
  
  th, td {
    padding: 16px;
    text-align: left;
    border-bottom: 1px solid var(--glass-border);
  }
  
  th {
    background: rgba(99, 102, 241, 0.05);
    font-weight: 600;
    color: var(--text-muted);
  }
  
  .actions {
    display: flex;
    gap: 10px;
  }
  
  button.icon-btn {
    background: transparent;
    border: none;
    color: var(--text-muted);
    cursor: pointer;
    transition: color 0.3s;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 8px;
    border-radius: 6px;
    
    &:hover {
      background: var(--input-bg);
      color: var(--text-main);
    }
    
    &.delete:hover {
      color: #ef4444;
      background: rgba(239, 68, 68, 0.1);
    }
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(0,0,0,0.5);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: var(--bg-card);
  width: 90%;
  max-width: 600px;
  border-radius: 16px;
  padding: 30px;
  border: 1px solid var(--glass-border);
  max-height: 90vh;
  overflow-y: auto;
  
  .modal-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
    
    h2 { font-size: 1.5rem; }
    
    button {
      background: transparent;
      border: none;
      color: var(--text-muted);
      cursor: pointer;
    }
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px;
  
  .form-group {
    display: flex;
    flex-direction: column;
    gap: 8px;
    
    label {
      font-weight: 500;
      color: var(--text-muted);
    }
    
    input, textarea, select {
      padding: 12px 16px;
      background: var(--input-bg);
      border: 1px solid var(--glass-border);
      border-radius: 8px;
      color: var(--text-main);
      font-family: inherit;
      font-size: 1rem;
      
      &:focus {
        outline: none;
        border-color: var(--primary);
      }
    }
    
    textarea {
      min-height: 120px;
      resize: vertical;
    }
  }
  
  .form-actions {
    display: flex;
    justify-content: flex-end;
    gap: 10px;
    margin-top: 10px;
  }
`;

const ManageTestimonials = () => {
    const [testimonials, setTestimonials] = useState([]);
    const [loading, setLoading] = useState(true);
    const [modalOpen, setModalOpen] = useState(false);
    const [currentTestimonial, setCurrentTestimonial] = useState({ name: '', company: '', content: '', rating: 5, status: 'active', image: '' });
    const [imageFile, setImageFile] = useState(null);
    const [isEditing, setIsEditing] = useState(false);

    const fetchTestimonials = async () => {
        try {
            const token = localStorage.getItem('adminToken');
            const response = await fetch(`${process.env.REACT_APP_API_URL || ''}/api/testimonials/admin`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await response.json();
            setTestimonials(Array.isArray(data) ? data : []);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchTestimonials();
    }, []);

    const handleOpenModal = (testimonial = null) => {
        if (testimonial) {
            setCurrentTestimonial(testimonial);
            setIsEditing(true);
        } else {
            setCurrentTestimonial({ name: '', company: '', content: '', rating: 5, status: 'active', image: '' });
            setIsEditing(false);
        }
        setImageFile(null);
        setModalOpen(true);
    };

    const handleCloseModal = () => {
        setModalOpen(false);
        setCurrentTestimonial({ name: '', company: '', content: '', rating: 5, status: 'active', image: '' });
        setImageFile(null);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const token = localStorage.getItem('adminToken');
        const url = isEditing
            ? `${process.env.REACT_APP_API_URL || ''}/api/testimonials/${currentTestimonial.id}`
            : `${process.env.REACT_APP_API_URL || ''}/api/testimonials`;
        const method = isEditing ? 'PUT' : 'POST';

        try {
            const formData = new FormData();
            Object.keys(currentTestimonial).forEach(key => {
                if (currentTestimonial[key] !== null) {
                    formData.append(key, currentTestimonial[key]);
                }
            });
            if (imageFile) {
                formData.append('image', imageFile);
            }

            const response = await fetch(url, {
                method,
                headers: {
                    'Authorization': `Bearer ${token}`
                },
                body: formData
            });

            if (response.ok) {
                fetchTestimonials();
                handleCloseModal();
            } else {
                alert('Failed to save testimonial');
            }
        } catch (err) {
            console.error(err);
            alert('Error connecting to server');
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to delete this testimonial?')) return;

        const token = localStorage.getItem('adminToken');
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL || ''}/api/testimonials/${id}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (response.ok) {
                fetchTestimonials();
            } else {
                alert('Failed to delete testimonial');
            }
        } catch (err) {
            console.error(err);
            alert('Error connecting to server');
        }
    };

    return (
        <div>
            <PageHeader>
                <h1>Manage Reputation</h1>
                <Button onClick={() => handleOpenModal()}><Plus size={20} /> Add Testimonial</Button>
            </PageHeader>

            {loading ? (
                <div><Loader2 className="spin" /> Loading...</div>
            ) : (
                <Table>
                    <thead>
                        <tr>
                            <th>Client Name</th>
                            <th>Company</th>
                            <th>Rating</th>
                            <th>Status</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {testimonials.map(t => (
                            <tr key={t.id}>
                                <td>{t.name}</td>
                                <td>{t.company}</td>
                                <td>
                                    <div style={{ display: 'flex', gap: '2px', color: '#fbbf24' }}>
                                        {[...Array(t.rating)].map((_, i) => <Star key={i} size={14} fill="#fbbf24" />)}
                                    </div>
                                </td>
                                <td>
                                    <span style={{
                                        padding: '4px 8px', borderRadius: '12px', fontSize: '0.8rem',
                                        background: t.status === 'active' ? 'rgba(34, 197, 94, 0.2)' : 'rgba(239, 68, 68, 0.2)',
                                        color: t.status === 'active' ? '#4ade80' : '#f87171'
                                    }}>
                                        {t.status}
                                    </span>
                                </td>
                                <td className="actions">
                                    <button className="icon-btn" onClick={() => handleOpenModal(t)}><Edit2 size={18} /></button>
                                    <button className="icon-btn delete" onClick={() => handleDelete(t.id)}><Trash2 size={18} /></button>
                                </td>
                            </tr>
                        ))}
                        {testimonials.length === 0 && (
                            <tr><td colSpan="5" style={{ textAlign: 'center', color: 'var(--text-muted)' }}>No testimonials found</td></tr>
                        )}
                    </tbody>
                </Table>
            )}

            {modalOpen && (
                <ModalOverlay onClick={handleCloseModal}>
                    <ModalContent onClick={e => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>{isEditing ? 'Edit Testimonial' : 'Add New Testimonial'}</h2>
                            <button onClick={handleCloseModal}><X size={24} /></button>
                        </div>

                        <Form onSubmit={handleSubmit}>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                                <div className="form-group">
                                    <label>Client Name</label>
                                    <input
                                        type="text"
                                        value={currentTestimonial.name}
                                        onChange={e => setCurrentTestimonial({ ...currentTestimonial, name: e.target.value })}
                                        required
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Company</label>
                                    <input
                                        type="text"
                                        value={currentTestimonial.company}
                                        onChange={e => setCurrentTestimonial({ ...currentTestimonial, company: e.target.value })}
                                        placeholder="e.g. Google"
                                    />
                                </div>
                            </div>

                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                                <div className="form-group">
                                    <label>Rating (1-5)</label>
                                    <select
                                        value={currentTestimonial.rating}
                                        onChange={e => setCurrentTestimonial({ ...currentTestimonial, rating: parseInt(e.target.value) })}
                                    >
                                        <option value="5">5 Stars</option>
                                        <option value="4">4 Stars</option>
                                        <option value="3">3 Stars</option>
                                        <option value="2">2 Stars</option>
                                        <option value="1">1 Star</option>
                                    </select>
                                </div>
                                <div className="form-group">
                                    <label>Status</label>
                                    <select
                                        value={currentTestimonial.status}
                                        onChange={e => setCurrentTestimonial({ ...currentTestimonial, status: e.target.value })}
                                    >
                                        <option value="active">Active</option>
                                        <option value="archived">Archived</option>
                                    </select>
                                </div>
                            </div>

                            <div className="form-group">
                                <label>Client Feedback</label>
                                <textarea
                                    value={currentTestimonial.content}
                                    onChange={e => setCurrentTestimonial({ ...currentTestimonial, content: e.target.value })}
                                    required
                                />
                            </div>

                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                                <div className="form-group">
                                    <label>Upload Client Photo</label>
                                    <input
                                        type="file"
                                        accept="image/*"
                                        onChange={e => setImageFile(e.target.files[0])}
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Or Set Photo URL Manually</label>
                                    <input
                                        type="text"
                                        value={currentTestimonial.image || ''}
                                        onChange={e => setCurrentTestimonial({ ...currentTestimonial, image: e.target.value })}
                                    />
                                </div>
                            </div>

                            <div className="form-actions">
                                <Button type="button" className="secondary" onClick={handleCloseModal}>Cancel</Button>
                                <Button type="submit">{isEditing ? 'Save Changes' : 'Add Testimonial'}</Button>
                            </div>
                        </Form>
                    </ModalContent>
                </ModalOverlay>
            )}
            <style>{`.spin { animation: spin 1s linear infinite; } @keyframes spin { 100% { transform: rotate(360deg); } }`}</style>
        </div>
    );
};

export default ManageTestimonials;
